/**
 * Multi-company branding and hostname routing.
 * Each company shares the same WorkManager codebase; branding and default URL differ.
 */

export const COMPANY_STORAGE_KEY = 'workmanager_company';

export const COMPANIES = [
  {
    id: 'fabworks',
    label: 'Fabworks',
    shortName: 'FAB WORKS',
    legalName: 'FabWorks LLC - GA',
    tagline: 'Complete work order and time tracking system',
    themePreset: 'fabworks',
    workUrl: 'https://fabworks.lightring.org',
    marketingSite: {
      url: 'https://fabworksga.com',
      label: 'fabworksga.com',
    },
    hostnames: [
      'fabworks.lightring.org',
      'workmanager.lightring.org',
      'dev.workmanager.lightring.org',
      'gsmgr.lightring.org',
      'localhost:3000',
      'localhost:3001',
      '127.0.0.1:3000',
    ],
    logos: {
      header: '/fabworks-logo-color.jpg',
      sidebar: '/fabworks-logo-bg.png',
      favicon: '/fabworks-logo-bg.png',
      print: '/fabworks-logo-color.jpg',
      plateOnDark: true,
    },
    print: {
      addressLines: ['1338 Ross Road', 'Shady Dale, GA 31085'],
    },
  },
  {
    id: 'langley',
    label: 'Langley Services',
    shortName: 'LANGLEY SERVICES',
    legalName: 'Langley Construction Services',
    tagline: 'Commercial & industrial duct installation & equipment setting',
    themePreset: 'langley',
    workUrl: 'https://langleyservices.lightring.org',
    marketingSite: {
      url: 'https://www.langley-services.com',
      label: 'langley-services.com',
    },
    hostnames: ['langleyservices.lightring.org'],
    logos: {
      header: '/langley-logo-v1.png',
      sidebar: '/langley-logo-v1.png',
      favicon: '/langley-logo-v1.png',
      print: '/langley-logo-v1.png',
      plateOnDark: false,
    },
    print: {
      addressLines: ['Greater Atlanta, GA'],
    },
  },
  {
    id: 'whw',
    label: 'WHW Industrial',
    shortName: 'WHW INDUSTRIAL',
    legalName: 'WHW Industrial Solutions',
    tagline: 'Industrial solutions work order and time tracking',
    themePreset: 'whw',
    workUrl: 'https://whwindustrial.lightring.org',
    hostnames: ['whwindustrial.lightring.org'],
    logos: {
      header: '/whw-logo-v1.png',
      sidebar: '/whw-logo-v1.png',
      favicon: '/whw-logo-v1.png',
      print: '/whw-logo-v1.png',
      plateOnDark: false,
    },
    print: {
      addressLines: [],
    },
  },
];

export const DEFAULT_COMPANY_ID = 'fabworks';

export const COMPANY_OPTIONS = COMPANIES.map(({ id, label }) => ({ id, label }));

export const isValidCompanyId = (id) => COMPANIES.some((c) => c.id === id);

export const getCompany = (id) =>
  COMPANIES.find((c) => c.id === id) || COMPANIES.find((c) => c.id === DEFAULT_COMPANY_ID);

export const normalizeHost = (value) => {
  if (!value) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '');
};

export const resolveCompanyFromHost = (hostname = '') => {
  const host = normalizeHost(hostname);
  if (!host) return null;
  return COMPANIES.find((c) => c.hostnames.some((h) => normalizeHost(h) === host)) || null;
};

export const resolveCompanyId = () => {
  if (typeof window === 'undefined') return DEFAULT_COMPANY_ID;

  const fromHost = resolveCompanyFromHost(window.location.host);
  if (fromHost) return fromHost.id;

  try {
    const stored = window.localStorage.getItem(COMPANY_STORAGE_KEY);
    if (isValidCompanyId(stored)) return stored;
  } catch {
    // ignore storage failures
  }

  return DEFAULT_COMPANY_ID;
};

export const readStoredCompanyId = () => {
  try {
    const stored = window.localStorage.getItem(COMPANY_STORAGE_KEY);
    return isValidCompanyId(stored) ? stored : null;
  } catch {
    return null;
  }
};

export const writeStoredCompanyId = (id) => {
  try {
    if (isValidCompanyId(id)) window.localStorage.setItem(COMPANY_STORAGE_KEY, id);
  } catch {
    // ignore storage failures
  }
};

export const shouldRedirectForCompany = (targetCompany) => {
  if (typeof window === 'undefined' || !targetCompany?.workUrl) return false;
  const host = normalizeHost(window.location.host);
  if (host.startsWith('localhost') || host.startsWith('127.0.0.1')) return false;
  return normalizeHost(window.location.origin) !== normalizeHost(targetCompany.workUrl);
};
